import { ResponseError } from '@marketplace-web/core-api/api-client-util'
import { api } from '@marketplace-web/core-api/core-api-client-util'

import { getCreateComplaintArgs } from '../transformers/complaint-creation'
import {
  CreateComplaintArgs,
  SubmitComplaintResp,
  GetComplaintCreationDetailsArgs,
  GetComplaintCreationDetailsResp,
} from '../types/complaint-creation'
import { ComplaintResp, SnadComplaintResp } from '../types/complaint'
import { GetEscrowOrderComplaintInfoResp } from '../types/escrow-order-complaint-info'
import {
  RefundOfferCreationDetailsDto,
  SendRefundOfferArgs,
  SendRefundOfferResp,
  RefundOfferIdDto,
} from '../types/refund-offer'
import { GetRequestReturnDetailsResp } from '../types/request-return-details'
import {
  CreateCustomReturnShipmentArgs,
  GetReturnShippingOptionsResp,
  ReturnLabelOrderResp,
} from '../types/return-shipment'
import { GetShipmentItemsResp } from '../types/shipment-items'
import { HandleRefundOfferActionArgs } from '../types/handle-refund-offer-action'
import {
  EscrowRefundEstimateResponse,
  GetEscrowRefundEstimateArgs,
} from '../types/escrow-refund-estimate'
import { GetIsNewComplaintViewResp } from '../types/is-new-complaint-view'
import { GetTransactionArgs, GetTransactionResp } from '../types/transaction'
import { EstimatedRefundValuesDto } from '../types/estimated-refund-values'

const moneyObjectHeader = {
  'X-Money-Object': 'true',
}

export const createComplaint = (args: CreateComplaintArgs) =>
  api.post<SubmitComplaintResp, ResponseError>('/complaints', getCreateComplaintArgs(args))

export const getComplaint = (id: string) => api.get<ComplaintResp>(`/complaints/${id}`)

export const getSnadComplaint = (transactionId: number) =>
  api.get<SnadComplaintResp>('/complaints/snad', {
    params: { transaction_id: transactionId },
  })

export const resolveComplaint = (id: string) => api.post(`/complaints/${id}/resolve`)

export const getComplaintCreationDetails = ({
  transactionId,
  params,
}: GetComplaintCreationDetailsArgs) =>
  api.get<GetComplaintCreationDetailsResp>(
    `transactions/${transactionId}/complaint_creation_details`,
    {
      params,
    },
  )

export const getEscrowOrderComplaintInfo = (orderId: number) =>
  api.get<GetEscrowOrderComplaintInfoResp>(`/escrow_orders/${orderId}/complaint_info`)

export const getEscrowRefundEstimate = ({
  orderId,
  orderItemIds,
  integratedShipping,
}: GetEscrowRefundEstimateArgs) =>
  api.get<EscrowRefundEstimateResponse>(`escrow_orders/${orderId}/complaint_refund_estimate`, {
    params: { order_item_ids: orderItemIds, integrated_shipping: integratedShipping },
  })

export const getRefundOfferCreationDetails = (complaintId: number) =>
  api.get<RefundOfferCreationDetailsDto>(`/complaints/${complaintId}/refund_offer_creation_details`)

export const getRefundOffersEstimate = (refundOfferId: number) =>
  api.get<EscrowRefundEstimateResponse>(`/refund_offers/${refundOfferId}/estimate`)

export const getRefundOfferId = (complaintId: number) =>
  api.get<RefundOfferIdDto>(`/complaints/${complaintId}/refund_offer_id`)

export const sendRefundOffer = ({ complaintId, refundOffer }: SendRefundOfferArgs) =>
  api.post<SendRefundOfferResp>('/refund_offers', {
    complaint_id: complaintId,
    refund_offer: { amount: refundOffer.amount, currency_code: refundOffer.currencyCode },
  })

export const handleRefundOffer = ({ refundOfferId, action }: HandleRefundOfferActionArgs) =>
  api.post(`/refund_offers/${refundOfferId}/${action}`)

export const createCustomReturnShipment = ({
  transactionId,
  returnShippingOption,
}: CreateCustomReturnShipmentArgs) =>
  api.post(`/transactions/${transactionId}/shipment/return_shipping_option`, {
    return_shipping_option: returnShippingOption,
  })

export const createReturnShipment = (transactionId: number) =>
  api.post(`/transactions/${transactionId}/shipment/return_shipment`)

export const getRequestReturnDetails = (transactionId: number) =>
  api.get<GetRequestReturnDetailsResp>(
    `/transactions/${transactionId}/shipment/request_return_details`,
  )

export const getReturnShippingOptions = (transactionId: number) =>
  api.get<GetReturnShippingOptionsResp>(
    `/transactions/${transactionId}/shipment/return_shipping_options`,
  )

export const getShipmentItems = (transactionId: number) =>
  api.get<GetShipmentItemsResp>(`/transactions/${transactionId}/shipment/items`)

export const prepareReturnLabelOrder = ({ transactionId }) =>
  api.post<ReturnLabelOrderResp>('/return_label/orders/prepare', {
    transaction_id: transactionId,
  })

export const getIsNewComplaintViewEnabled = (faqEntryId: number) =>
  api.get<GetIsNewComplaintViewResp>(`faq_entries/${faqEntryId}/complaint_view`)

export const getTransaction = ({ id }: GetTransactionArgs) =>
  api.get<GetTransactionResp>(`/transactions/${id}`, { headers: moneyObjectHeader })

export const getEstimatedRefundValues = (complaintId: number) =>
  api.get<EstimatedRefundValuesDto>(`/complaints/${complaintId}/estimated_refund_values`)
